<template>
	<div class="carousel-wrapper">
		<button
			class="nav-arrow left"
			:disabled="isPrevDisabled"
			:class="{ disabled: isPrevDisabled }"
			aria-label="Previous slide"
			@click="scrollPrev"
		>
			<!-- ‹ -->
			<img src="@/assets/images/backgroundServices/arrow_left.svg" alt="<" />
		</button>
		<div
			ref="slider"
			class="slider"
			@scroll="handleScroll"
			@mouseenter="pauseAutoplay"
			@mouseleave="resumeAutoplay"
		>
			<!-- Ghost before -->
			<div class="ghost"></div>

			<!-- images -->
			<div
				v-for="(img, i) in images"
				:key="i"
				class="item"
				:class="{ active: activeIndex === i }"
				alt="carousel image"
			>
				<img :src="img" alt="" />
			</div>

			<!-- Ghost after -->
			<div class="ghost"></div>
		</div>

		<button
			class="nav-arrow right"
			:disabled="isNextDisabled"
			:class="{ disabled: isNextDisabled }"
			aria-label="Next slide"
			@click="scrollNext"
		>
			<!-- › -->
			<img src="@/assets/images/backgroundServices/arrow_right.svg" alt=">" />
		</button>
		<!-- Dots -->
		<div class="dots">
			<span
				v-for="(dot, i) in images"
				:key="i"
				class="dot"
				:class="{ active: activeIndex === i }"
				@click="scrollToIndex(i)"
			></span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";

const props = defineProps<{
	images: string[];
	autoplayInterval?: number;
}>();

const slider = ref<HTMLDivElement | null>(null);
const activeIndex = ref(0);
const autoplayTimer = ref<number | null>(null);
let userScrolling = false;

const isPrevDisabled = computed(() => activeIndex.value === 0);
const isNextDisabled = computed(
	() => activeIndex.value === props.images.length - 1,
);

/* ---------------------------
   FIND WHICH PICTURE IS CLOSEST TO THE CENTER
---------------------------- */
function updateActive() {
	const container = slider.value;
	if (!container) return;

	const center = container.scrollLeft + container.offsetWidth / 2;
	const items = Array.from(container.children) as HTMLElement[];

	let closest = 0;
	let smallest = Infinity;

	// skip ghost elements
	for (let i = 1; i < items.length - 1; i++) {
		const item = items[i];
		const itemCenter = item.offsetLeft + item.offsetWidth / 2;
		const dist = Math.abs(center - itemCenter);

		if (dist < smallest) {
			smallest = dist;
			closest = i - 1; // first index image
		}
	}

	activeIndex.value = closest;
}

/* ---------------------------
   SCROLL TO SPECIFIC IMAGE
---------------------------- */
function scrollToIndex(index: number) {
	const container = slider.value;
	if (!container) return;

	const realIdx = index + 1; // because of the ghost
	const item = container.children[realIdx] as HTMLElement;

	const offset = item.offsetLeft;
	const width = item.offsetWidth;
	const containerWidth = container.offsetWidth;

	const target = offset - containerWidth / 2 + width / 2;

	container.scrollTo({
		left: target,
		behavior: "smooth",
	});
}

/* ---------------------------
   AUTOPLAY
---------------------------- */
function startAutoplay() {
	const interval = props.autoplayInterval ?? 2500;

	autoplayTimer.value = window.setInterval(() => {
		if (userScrolling) return;

		scrollToIndex((activeIndex.value + 1) % props.images.length);
	}, interval);
}

function pauseAutoplay() {
	userScrolling = true;
}
function resumeAutoplay() {
	userScrolling = false;
}

function stopAutoplay() {
	if (autoplayTimer.value) clearInterval(autoplayTimer.value);
}

/* ---------------------------
   SCROLL LISTENER
---------------------------- */
let scrollTimeout: number | null = null;

function handleScroll() {
	updateActive();

	userScrolling = true;
	if (scrollTimeout) clearTimeout(scrollTimeout);

	scrollTimeout = window.setTimeout(() => {
		userScrolling = false;
	}, 1200);
}

function scrollPrev() {
	if (!slider.value) return;

	// Scroll to the previous image (if not on the first one)
	let prevIndex = activeIndex.value - 1;
	if (prevIndex < 0) prevIndex = 0;
	scrollToIndex(prevIndex);
}

function scrollNext() {
	if (!slider.value) return;

	// Scroll to the next image (if not on the last one)
	let nextIndex = activeIndex.value + 1;
	if (nextIndex >= props.images.length) nextIndex = props.images.length - 1;
	scrollToIndex(nextIndex);
}
/* ----------------------
   INFINITE SCROLL 
 ------------------------ */
// it is also necessary to remove the disabling of arrows

// function scrollPrev() {
//   if (!slider.value) return;

//   let prevIndex = activeIndex.value - 1;
//   if (prevIndex < 0) {
//     prevIndex = props.images.length - 1; // vrati na poslednju sliku
//   }
//   scrollToIndex(prevIndex);
// }

// function scrollNext() {
//   if (!slider.value) return;

//   let nextIndex = activeIndex.value + 1;
//   if (nextIndex >= props.images.length) {
//     nextIndex = 0; // vrati na prvu sliku
//   }
//   scrollToIndex(nextIndex);
// }

onMounted(() => {
	updateActive();
	startAutoplay();
});

onBeforeUnmount(() => {
	stopAutoplay();
});
</script>

<style scoped>
.carousel-wrapper {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
}

/* SLIDER */
.slider {
	display: flex;
	overflow-x: scroll;
	scroll-snap-type: x mandatory;
	gap: 20px;
	padding: 0;
	width: 100%;
	scrollbar-width: none;
}
.slider::-webkit-scrollbar {
	display: none;
}

.nav-arrow {
	position: absolute;
	top: 50%;
	top: calc(50% - 20px);
	transform: translateY(-50%);
	background: rgba(0, 0, 0, 0.048);
	border: none;

	/* color: white; */
	font-size: 2.5rem;
	line-height: 1;
	width: 40px;
	height: 60px;
	border-radius: 8px;
	cursor: pointer;
	user-select: none;
	opacity: 0;
	transition: opacity 0.3s ease;
	z-index: 10;
	display: none; /* hidden by default */
}

.nav-arrow.left {
	left: 5px;
}

.nav-arrow.right {
	right: 5px;
}
.nav-arrow.disabled,
.nav-arrow:disabled {
	opacity: 0.4;
	cursor: not-allowed;
	pointer-events: none;
}

/* RESPONSIVE WIDTH BY BREAKPOINTS */

/* Mobile – 90% */
@media (max-width: 768px) {
	.item {
		flex: 0 0 90%;
	}
	.ghost {
		flex: 0 0 calc((100% - 90%) / 2);
	}
	.nav-arrow {
		display: block;
		opacity: 1;
	}
}

/* Tablet – 70% */
@media (min-width: 768px) and (max-width: 1024px) {
	.item {
		flex: 0 0 70%;
	}
	.ghost {
		flex: 0 0 calc((100% - 70%) / 2);
	}
}

/* Desktop – 50% */
@media (min-width: 1024px) {
	.item {
		flex: 0 0 60%;
	}
	.ghost {
		flex: 0 0 calc((100% - 50%) / 2);
	}
}

/* @media (max-width: 768px) {
	.nav-arrow {
		display: block;
		opacity: 1;
	}
} */

/* IMAGES — responsive height */
.item {
	/* 16 / 9 */
	aspect-ratio: 16 / 6;

	/* @media (max-width: 768px) {
        aspect-ratio: 16 / 9;
    } */

	display: flex;
	background: #344e59;
	border-radius: 24px;
	transition:
		filter 0.4s ease,
		transform 0.4s ease,
		opacity 0.4s ease;
	filter: brightness(50%);
	transform: scale(0.85);
	opacity: 0.5;

	img {
		width: 100%;
		height: auto;
		object-fit: cover;
		border-radius: 12px;
		scroll-snap-align: center;
	}
}

.item.active {
	filter: brightness(100%);
	transform: scale(1);
	opacity: 1;
	background: #223239;

	/* border: 1px solid;
border-image-source: linear-gradient(96.96deg, rgba(255, 255, 255, 0.2) 0.03%, rgba(255, 255, 255, 0.1) 99.97%) ;
border-image-slice: 1;
border-radius: 24px; */
}

/* DOTS */
.dots {
	margin-top: 18px;
	display: flex;
	gap: 8px;
}

.dot {
	width: 10px;
	height: 10px;
	background: #777;
	border-radius: 50%;
	transition: 0.3s ease;
	cursor: pointer;
	opacity: 0.5;
}

.dot.active {
	width: 22px;
	border-radius: 8px;

	/* background: #fff;
	opacity: 1; */
}
</style>
